import React, { useState } from 'react';
import { Box, Button, Checkbox, Chip, FormControlLabel, Grid, IconButton, Tooltip } from '@mui/material';
import Typography from '@mui/material/Typography';
import { ContentCopy, Edit } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { AccountData, CatalogItemOut, CatalogItemSortBy, CatalogService, SortDirection } from '../api';
import { CellProps, TableSortState, useTable } from '../table/useTable';
import { MultiSelectFilter } from '../common/ui/MultiSelectFilter';
import { useAuth } from '../session/InternalAuthProvider';
import { useLanguage } from '../common/GeneralUtils';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { localizedLabel } from '../utils/maskUtils';
import { CatalogItemPhoto } from './CatalogItemPhoto';
import { priceFormatterFromCurrency } from '../utils/utils';
import { useCatalogCategories } from './swr/useCatalogCategories';
import { useCatalogItems } from './swr/useCatalogItems';
import { CatalogItemDrawerType, collectExistingTags } from './catalogUtils';
import { EditCatalogItemDrawer } from './EditCatalogItemDrawer';

type CatalogItemsFilterState = {
  categoryIds?: string[];
  tags?: string[];
  showArchived?: boolean;
};

// Every filter and the sort are the server's job -- nothing is narrowed or reordered here.
export const catalogItemsFetcher = async ({
  search,
  filterState,
  sort,
}: {
  search?: string;
  page: number;
  perPage: number;
  filterState?: object;
  sort?: TableSortState;
}) => {
  const itemsFilter = (filterState ?? {}) as CatalogItemsFilterState;
  return CatalogService.getCatalogItems(
    1,
    500,
    search,
    itemsFilter.categoryIds?.length ? itemsFilter.categoryIds : undefined,
    itemsFilter.tags?.length ? itemsFilter.tags : undefined,
    undefined,
    undefined,
    itemsFilter.showArchived,
    sort?.sortBy as CatalogItemSortBy | undefined,
    sort?.sortDirection as SortDirection | undefined,
  );
};

export const CatalogItemsTab = () => {
  const { t } = useTranslation();
  // @ts-ignore
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const language = useLanguage();
  const { readOnly } = UseAuthPermissions();
  const { categories } = useCatalogCategories();
  // Unfiltered catalog, so the tag list stays complete while a tag filter is applied.
  const { items: allItems, mutate: mutateAllItems } = useCatalogItems();
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [showArchived, setShowArchived] = useState(false);
  const [drawerIsOpen, setDrawerIsOpen] = useState(false);
  const [drawerType, setDrawerType] = useState<CatalogItemDrawerType>(CatalogItemDrawerType.CREATE);
  const [selectedItem, setSelectedItem] = useState<CatalogItemOut | undefined>(undefined);
  const openDrawer = (type: CatalogItemDrawerType, item?: CatalogItemOut) => {
    setDrawerType(type);
    setSelectedItem(item);
    setDrawerIsOpen(true);
  };
  // Alphabetical, not the admin sort_order used on the categories screen: this is a filter to
  // find one entry in, so scanning beats curated ordering.
  const activeCategories = categories
    .filter((category) => !category.archived)
    .sort((first, second) =>
      localizedLabel(first.label, language).localeCompare(localizedLabel(second.label, language), language),
    );
  const categoryLabelById = new Map(categories.map((category) => [category.id, category.label]));

  const renderPrice = (item: CatalogItemOut) =>
    priceFormatterFromCurrency(item.price ?? 0, account.currency, undefined, false, false);

  const tableDetails: CellProps<CatalogItemOut>[] = [
    {
      id: 'edit',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (item: CatalogItemOut) => (
        <Tooltip title={readOnly ? t('noPermissions') : t('edit')}>
          <span>
            <IconButton
              disabled={readOnly}
              className="action-icon"
              onClick={() => openDrawer(CatalogItemDrawerType.EDIT, item)}>
              <Edit />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
    {
      id: 'photo',
      alignment: 'left',
      width: 84,
      render: (item: CatalogItemOut) => (
        <Box sx={{ my: 0.5 }}>
          <CatalogItemPhoto photoUrl={item.photo_url} alt={localizedLabel(item.name, language)} size={60} />
        </Box>
      ),
    },
    {
      // Headers render at flex-start, so cell content stays left too or the label floats away
      // from its own column.
      id: 'name',
      alignment: 'left',
      label: t('catalog.itemName'),
      render: (item: CatalogItemOut) => (
        <Grid container gap={1} alignItems="center">
          <Typography>{localizedLabel(item.name, language)}</Typography>
          {item.archived && <Chip size="small" label={t('catalog.archived')} />}
        </Grid>
      ),
    },
    {
      id: 'category',
      alignment: 'left',
      label: t('catalog.category'),
      render: (item: CatalogItemOut) => localizedLabel(categoryLabelById.get(item.category_id), language),
    },
    {
      // 'price' is reserved by TableRow (renders the orders/vouchers PriceCell)
      id: 'itemPrice',
      alignment: 'left',
      label: t('catalog.price'),
      render: renderPrice,
      sortKey: CatalogItemSortBy.PRICE,
    },
    {
      id: 'tags',
      alignment: 'left',
      label: t('catalog.tags'),
      render: (item: CatalogItemOut) => (
        <Grid container gap={0.5}>
          {(item.tags ?? []).map((tag) => (
            <Chip key={tag} size="small" variant="outlined" label={tag} />
          ))}
        </Grid>
      ),
    },
    {
      // Cloning is how a near-identical item gets created: the drawer opens prefilled and posts
      // a new item, so the source row is never touched.
      id: 'clone',
      alignment: 'left',
      width: 30,
      noPadding: true,
      render: (item: CatalogItemOut) => (
        <Tooltip title={readOnly ? t('noPermissions') : t('catalog.cloneItem')}>
          <span>
            <IconButton
              disabled={readOnly}
              className="action-icon"
              onClick={() => openDrawer(CatalogItemDrawerType.CLONE, item)}>
              <ContentCopy />
            </IconButton>
          </span>
        </Tooltip>
      ),
    },
  ];

  const existingTags = collectExistingTags(allItems);

  const { mutate, data, TableComponent } = useTable<CatalogItemOut>({
    fillHeight: true,
    actionsComponent: (
      <Grid container gap={1.5} alignItems="center" justifyContent="flex-end" flexWrap="wrap">
        <MultiSelectFilter
          label={t('catalog.category')}
          emptyLabel={t('catalog.allCategories')}
          value={categoryFilters}
          onChange={setCategoryFilters}
          options={activeCategories.map((category) => ({
            value: category.id,
            label: localizedLabel(category.label, language),
          }))}
        />
        {!!existingTags.length && (
          <MultiSelectFilter
            label={t('catalog.tags')}
            emptyLabel={t('catalog.allTags')}
            minWidth="150px"
            value={tagFilters}
            onChange={setTagFilters}
            options={existingTags.map((tag) => ({ value: tag, label: tag }))}
          />
        )}
        <FormControlLabel
          control={<Checkbox size="small" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />}
          label={<Typography variant="body2">{t('catalog.showArchived')}</Typography>}
        />
        <Tooltip
          title={
            readOnly ? t('noPermissions') : !activeCategories.length ? t('catalog.createCategoryFirst') : undefined
          }>
          <span>
            <Button
              disabled={readOnly || !activeCategories.length}
              variant="contained"
              sx={{ textTransform: 'none', marginInlineStart: '8px' }}
              onClick={() => openDrawer(CatalogItemDrawerType.CREATE)}>
              {t('catalog.addItem')}
            </Button>
          </span>
        </Tooltip>
      </Grid>
    ),
    defaultRowsPerPage: 500,
    hidePaging: true,
    opId: 'getCatalogItems',
    fetcher: catalogItemsFetcher,
    filterState: { categoryIds: categoryFilters, tags: tagFilters, showArchived },
    tableDetails,
    splitColumnsOnWideScreen: true,
    revalidateOnFocus: !drawerIsOpen,
    getRowSx: (item: CatalogItemOut) => (item.archived ? { opacity: 0.55 } : undefined),
  });

  return (
    <Grid container flexDirection="column" flexWrap="nowrap" sx={{ flex: 1, minHeight: 0 }}>
      {TableComponent}
      {drawerIsOpen && (
        <EditCatalogItemDrawer
          isOpen={drawerIsOpen}
          onClose={() => setDrawerIsOpen(false)}
          type={drawerType}
          item={selectedItem}
          categories={categories}
          existingTags={existingTags}
          defaultCategoryId={categoryFilters.length === 1 ? categoryFilters[0] : undefined}
          onSaved={(savedItem: CatalogItemOut) => {
            void mutate([...(data || []).filter((item) => item.id !== savedItem.id), savedItem]);
            // Separate cache entry; without this a brand new tag never reaches the filter.
            void mutateAllItems();
          }}
        />
      )}
    </Grid>
  );
};
